import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';

const CompromisosPagoAdminWidget = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [compromisosPrioritarios, setCompromisosPrioritarios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadCompromisos();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadCompromisos = async () => {
    try {
      const response = await axios.get(`${API}/admin/compromisos-pago`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        setResumen(response.data.resumen);
        // Mostrar solo los más urgentes (vencidos y por verificar)
        const prioritarios = response.data.compromisos
          .filter(c => c.estado === 'pendiente_verificacion' || c.vencido || (c.dias_restantes !== undefined && c.dias_restantes <= 3))
          .slice(0, 5);
        setCompromisosPrioritarios(prioritarios);
      }
    } catch (error) {
      console.error('Error loading compromisos:', error);
    } finally {
      setLoading(false);
    }
  };

  // No mostrar si no es admin
  if (!user || user.rol !== 'admin') return null;
  if (loading) return null;
  
  // No mostrar si no hay compromisos activos
  if (!resumen || resumen.total === 0) return null;
  
  // No mostrar si solo hay compromisos pagados
  const activos = (resumen.pendientes || 0) + (resumen.por_verificar || 0) + (resumen.vencidos || 0);
  if (activos === 0) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-UY', {
        day: '2-digit',
        month: 'short'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="border-amber-200" data-testid="compromisos-pago-admin-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <i className="fas fa-handshake text-amber-600"></i>
            Compromisos de Pago
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/app/gestion-compromisos-pago')}
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
          >
            Ver todos <i className="fas fa-arrow-right ml-1"></i>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Resumen */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="text-center p-2 bg-yellow-50 rounded-lg">
            <div className="text-xl font-bold text-yellow-600">{resumen.pendientes || 0}</div>
            <div className="text-xs text-gray-600">Pendientes</div>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded-lg">
            <div className="text-xl font-bold text-blue-600">{resumen.por_verificar || 0}</div>
            <div className="text-xs text-gray-600">Por Verificar</div>
          </div>
          <div className="text-center p-2 bg-red-50 rounded-lg">
            <div className="text-xl font-bold text-red-600">{resumen.vencidos || 0}</div>
            <div className="text-xs text-gray-600">Vencidos</div>
          </div>
          <div className="text-center p-2 bg-green-50 rounded-lg">
            <div className="text-xl font-bold text-green-600">{resumen.pagados || 0}</div>
            <div className="text-xs text-gray-600">Pagados</div>
          </div>
        </div>

        {/* Lista de compromisos prioritarios */}
        {compromisosPrioritarios.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <i className="fas fa-exclamation-circle text-amber-500"></i>
              Requieren Atención
            </h4>
            {compromisosPrioritarios.map((compromiso) => (
              <div
                key={compromiso.id}
                className={`flex items-center justify-between p-2 rounded-lg border ${
                  compromiso.estado === 'pendiente_verificacion'
                    ? 'bg-blue-50 border-blue-200'
                    : compromiso.vencido
                      ? 'bg-red-50 border-red-200'
                      : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {compromiso.estudiante_nombre}
                  </p>
                  <p className="text-xs text-gray-500">
                    ${compromiso.monto?.toFixed(2)} - {formatDate(compromiso.fecha_vencimiento)}
                  </p>
                </div>
                <div>
                  {compromiso.estado === 'pendiente_verificacion' ? (
                    <Badge className="bg-blue-500 text-white text-xs">
                      <i className="fas fa-eye mr-1"></i>Verificar
                    </Badge>
                  ) : compromiso.vencido ? (
                    <Badge className="bg-red-500 text-white text-xs">
                      <i className="fas fa-exclamation-triangle mr-1"></i>Vencido
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500 text-white text-xs">
                      {compromiso.dias_restantes}d
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Botón para ver panel completo si hay más */}
        {activos > 5 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/app/gestion-compromisos-pago')}
            className="w-full mt-3 border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            Ver {activos - 5} compromisos más
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default CompromisosPagoAdminWidget;
